import { useState, useEffect } from 'react';
import api from '../../services/api';
import CreateStudentModal from '../../components/admin/CreateStudentModal';
import ManageAccessModal from '../../components/admin/ManageAccessModal';
import { Search, Plus } from 'lucide-react';
import { motion } from 'framer-motion';

export default function AdminStudentsPage() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    loadStudents();
  }, []);

  async function loadStudents() {
    try {
      const { data } = await api.get('/students');
      console.log('📚 Alunos carregados:', data);
      setStudents(data);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleStudentCreated(response) {
    console.log('📥 Aluno criado recebido:', response);
    // O backend retorna { user, profile }
    // Adicionar o profile na lista
    if (response.profile) {
      setStudents([response.profile, ...students]);
      console.log('✅ Aluno adicionado à lista');
    }
  }

  function handleManageAccess(student) {
    console.log('🔑 Gerenciar acesso para:', student);
    setSelectedStudent(student);
    setIsAccessModalOpen(true);
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-text-primary mb-3">Gestão de Usuários</h1>
          <p className="text-gray-600 dark:text-text-secondary text-lg">Gerencie acessos e acompanhe o progresso</p>
        </div>
        <div className="flex gap-4 w-full md:w-auto">
          <div className="relative flex-1 md:w-80">
            <Search className="absolute left-4 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar aluno..."
              className="w-full bg-background-800 dark:bg-background-800 border border-gray-700 dark:border-gray-700 rounded-2xl py-3 pl-11 pr-4 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary placeholder-gray-500"
            />
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg shadow-primary-500/20 transition-all duration-300 hover:scale-105 flex items-center gap-2 whitespace-nowrap"
          >
            <Plus size={20} />
            Novo Usuário
          </button>
        </div>
      </div>

      {students.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-12 bg-background-800 dark:bg-background-800 border border-gray-700 dark:border-gray-700 rounded-3xl"
        >
          <p className="text-text-secondary">
            Nenhum aluno cadastrado
          </p>
        </motion.div>
      ) : (
        <div className="bg-background-800 dark:bg-background-800 border border-gray-700 dark:border-gray-700 rounded-3xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700 dark:border-gray-700">
                <th className="text-left p-6 text-text-secondary font-semibold">Usuário</th>
                <th className="text-left p-6 text-text-secondary font-semibold">Email</th>
                <th className="text-left p-6 text-text-secondary font-semibold">Tipo</th>
                <th className="text-left p-6 text-text-secondary font-semibold">Status</th>
                <th className="text-right p-6"></th>
              </tr>
            </thead>
            <tbody>
              {students.map((student, index) => (
                <motion.tr
                  key={student.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-gray-700 dark:border-gray-700 last:border-none hover:bg-gray-700/50 dark:hover:bg-gray-700/50 transition"
                >
                  <td className="p-6 font-medium text-text-primary">{student.full_name}</td>
                  <td className="p-6 text-text-secondary">{student.email}</td>
                  <td className="p-6">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      student.role === 'admin' || student.role === 'superadmin'
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {student.role === 'superadmin' ? 'Super Admin' : student.role === 'admin' ? 'Admin' : 'Aluno'}
                    </span>
                  </td>
                  <td className="p-6">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      student.is_active
                        ? 'bg-success/20 text-success'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {student.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="p-6 text-right">
                    {student.role === 'student' && (
                      <button 
                        onClick={() => handleManageAccess(student)}
                        className="text-primary-500 hover:text-primary-400 font-medium transition"
                      >
                        Gerenciar Acesso
                      </button>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de criar aluno */}
      <CreateStudentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleStudentCreated}
      />

      {/* Modal de gerenciar acesso */}
      <ManageAccessModal
        isOpen={isAccessModalOpen}
        onClose={() => {
          setIsAccessModalOpen(false);
          setSelectedStudent(null);
        }}
        student={selectedStudent}
      />
    </div>
  );
}
